import React, { useState } from "react";
import { Helmet } from "react-helmet-async";
import TawkTo from "./TawkTo";
import {
  trackNavigation,
  trackServiceInteraction,
  trackFormInteraction,
  trackSliderInteraction,
  trackExternalLink,
  trackCTAClick
} from "../utils/analytics";

// Why Choose Slider Component
const WhyChooseSlider = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [previousSlide, setPreviousSlide] = useState(0);
  const [direction, setDirection] = useState('next');
  const [isTransitioning, setIsTransitioning] = useState(false);

  const slides = [
    {
      title: "14+ Years",
      description: "Enterprise program management experience optimizing workflows for teams ranging from 10 to 500+ members",
      gradient: "from-cyan-500 to-blue-500"
    },
    {
      title: "50+ Automations Built",
      description: "Custom Jira automations and workflows that save teams 100+ hours every month",
      gradient: "from-violet-500 to-fuchsia-500"
    },
    {
      title: "Real Results",
      description: "Teams see 40% faster sprint planning and 60% reduction in manual reporting time",
      gradient: "from-fuchsia-500 to-pink-500"
    }
  ];

  const changeSlide = (newIndex, dir) => {
    setPreviousSlide(currentSlide);
    setDirection(dir);
    setIsTransitioning(true);
    setCurrentSlide(newIndex);

    setTimeout(() => {
      setIsTransitioning(false);
    }, 600);
  };

  const nextSlide = () => {
    const newIndex = (currentSlide + 1) % slides.length;
    trackSliderInteraction('next', newIndex, slides[newIndex].title);
    changeSlide(newIndex, 'next');
  };

  const prevSlide = () => {
    const newIndex = (currentSlide - 1 + slides.length) % slides.length;
    trackSliderInteraction('prev', newIndex, slides[newIndex].title);
    changeSlide(newIndex, 'prev');
  };

  const goToSlide = (index) => {
    if (index !== currentSlide) {
      trackSliderInteraction('dot_click', index, slides[index].title);
      changeSlide(index, index > currentSlide ? 'next' : 'prev');
    }
  };

  // Auto-scroll every 3 seconds
  React.useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSlide((prev) => {
        const newIndex = (prev + 1) % slides.length;
        trackSliderInteraction('auto_scroll', newIndex, slides[newIndex].title);
        setPreviousSlide(prev);
        setDirection('next');
        setIsTransitioning(true);

        setTimeout(() => {
          setIsTransitioning(false);
        }, 600);

        return newIndex;
      });
    }, 3000);

    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="relative">
      <style>{`
        @keyframes slideInFromRight {
          from {
            transform: translateX(100%);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }

        @keyframes slideOutToLeft {
          from {
            transform: translateX(0);
            opacity: 1;
          }
          to {
            transform: translateX(-100%);
            opacity: 0;
          }
        }

        @keyframes slideInFromLeft {
          from {
            transform: translateX(-100%);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }

        @keyframes slideOutToRight {
          from {
            transform: translateX(0);
            opacity: 1;
          }
          to {
            transform: translateX(100%);
            opacity: 0;
          }
        }

        .slide-enter-next {
          animation: slideInFromRight 0.6s ease-out forwards;
        }

        .slide-exit-next {
          animation: slideOutToLeft 0.6s ease-out forwards;
        }

        .slide-enter-prev {
          animation: slideInFromLeft 0.6s ease-out forwards;
        }

        .slide-exit-prev {
          animation: slideOutToRight 0.6s ease-out forwards;
        }
      `}</style>

      {/* Slide Container */}
      <div className="relative overflow-hidden rounded-3xl bg-white/5 backdrop-blur-xl border border-white/10 min-h-[320px] flex items-center justify-center">
        {/* Previous Slide - Exiting */}
        {isTransitioning && (
          <div
            className={`absolute inset-0 flex items-center justify-center ${
              direction === 'next' ? 'slide-exit-next' : 'slide-exit-prev'
            }`}
          >
            <div className="text-center px-12 lg:px-16">
              <div className={`text-4xl lg:text-6xl font-black bg-gradient-to-r ${slides[previousSlide].gradient} bg-clip-text text-transparent mb-8`} style={{ fontFamily: 'Sora, sans-serif' }}>
                {slides[previousSlide].title}
              </div>
              <p className="text-white/90 text-xl lg:text-2xl leading-relaxed max-w-2xl mx-auto" style={{ fontFamily: 'Space Grotesk, sans-serif', fontWeight: 300 }}>
                {slides[previousSlide].description}
              </p>
            </div>
          </div>
        )}

        {/* Current Slide - Entering */}
        <div
          className={`${isTransitioning ? 'absolute inset-0 flex items-center justify-center' : ''} ${
            isTransitioning
              ? (direction === 'next' ? 'slide-enter-next' : 'slide-enter-prev')
              : ''
          }`}
        >
          <div className="text-center px-12 lg:px-16">
            <div className={`text-4xl lg:text-6xl font-black bg-gradient-to-r ${slides[currentSlide].gradient} bg-clip-text text-transparent mb-8`} style={{ fontFamily: 'Sora, sans-serif' }}>
              {slides[currentSlide].title}
            </div>
            <p className="text-white/90 text-xl lg:text-2xl leading-relaxed max-w-2xl mx-auto" style={{ fontFamily: 'Space Grotesk, sans-serif', fontWeight: 300 }}>
              {slides[currentSlide].description}
            </p>
          </div>
        </div>
      </div>

      {/* Navigation Buttons */}
      <button
        onClick={prevSlide}
        className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 lg:-translate-x-6 w-12 h-12 rounded-full bg-white/10 backdrop-blur-md border border-white/20 flex items-center justify-center text-white hover:bg-white/20 transition-all duration-300 hover:scale-110"
        aria-label="Previous slide"
      >
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
        </svg>
      </button>

      <button
        onClick={nextSlide}
        className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 lg:translate-x-6 w-12 h-12 rounded-full bg-white/10 backdrop-blur-md border border-white/20 flex items-center justify-center text-white hover:bg-white/20 transition-all duration-300 hover:scale-110"
        aria-label="Next slide"
      >
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
        </svg>
      </button>

      {/* Dots Indicator */}
      <div className="flex justify-center gap-3 mt-8">
        {slides.map((_, index) => (
          <button
            key={index}
            onClick={() => goToSlide(index)}
            className={`transition-all duration-300 rounded-full ${
              currentSlide === index
                ? 'w-12 h-3 bg-gradient-to-r from-cyan-400 to-fuchsia-400'
                : 'w-3 h-3 bg-white/30 hover:bg-white/50'
            }`}
            aria-label={`Go to slide ${index + 1}`}
          />
        ))}
      </div>
    </div>
  );
};

const ZykainWebsite = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [scrollPosition, setScrollPosition] = useState(0);
  const [isProgrammaticScroll, setIsProgrammaticScroll] = useState(false);
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    message: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [showToast, setShowToast] = useState(false);

  React.useEffect(() => {
    let ticking = false;

    const handleScroll = () => {
      // Don't update navbar state during programmatic scrolling
      if (isProgrammaticScroll) return;

      if (!ticking) {
        window.requestAnimationFrame(() => {
          const scrollPosition = window.scrollY;
          setIsScrolled(scrollPosition > 20);
          ticking = false;
        });
        ticking = true;
      }
    };

    // Set initial state
    handleScroll();

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [isProgrammaticScroll]);

  React.useEffect(() => {
    // Lock/unlock body scroll when mobile menu is open
    if (mobileMenuOpen) {
      // Save current scroll position
      const currentScrollY = window.scrollY;
      setScrollPosition(currentScrollY);

      // Lock body scroll and maintain position
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.top = `-${currentScrollY}px`;
      document.body.style.width = '100%';
    } else {
      // Restore scroll position
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';

      // Restore the scroll position immediately
      window.scrollTo(0, scrollPosition);

      // Mobile Safari transform reset fix
      const mainBurgerLines = document.querySelectorAll('.main-burger-line');
      mainBurgerLines.forEach((line, index) => {
        // Force hardware acceleration reset on mobile Safari
        line.style.transform = 'translateZ(0)';
        line.style.webkitTransform = 'translateZ(0)';

        // Reset to correct positions after a frame
        requestAnimationFrame(() => {
          if (index === 0) {
            line.style.transform = 'translateY(-8.48px)';
            line.style.webkitTransform = 'translateY(-8.48px)';
          } else if (index === 1) {
            line.style.transform = 'translateY(0px)';
            line.style.webkitTransform = 'translateY(0px)';
          } else if (index === 2) {
            line.style.transform = 'translateY(8.48px)';
            line.style.webkitTransform = 'translateY(8.48px)';
          }
        });
      });
    }

    // Cleanup on unmount
    return () => {
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
    };
  }, [mobileMenuOpen, scrollPosition]);

  React.useEffect(() => {
    // Force a re-render of hamburger button when menu state changes
    if (!mobileMenuOpen) {
      // Small delay to ensure smooth transition
      setTimeout(() => {
        // This forces the component to re-evaluate the hamburger state
      }, 50);
    }
  }, [mobileMenuOpen]);

  const handleMenuClose = () => {
    setMobileMenuOpen(false);
  };

  const handleSmoothScroll = (targetId, source = 'navbar') => {
    // Track navigation event
    trackNavigation(targetId, source);

    // Close mobile menu first
    setMobileMenuOpen(false);

    // Immediately set navbar to scrolled state (white/opaque) for mobile navigation
    const isMobileView = window.innerWidth < 768;
    if (isMobileView) {
      setIsScrolled(true);
    }

    // Flag that we're doing a programmatic scroll - keeps navbar frozen
    setIsProgrammaticScroll(true);

    // Wait for menu to close, then scroll
    setTimeout(() => {
      const element = document.getElementById(targetId);
      if (element) {
        // Check if mobile view - apply gap for any mobile navigation
        const isMobile = window.innerWidth < 768;

        if (isMobile) {
          // Mobile: scroll with 40px gap below navbar for all navigation
          const offset = 40; // Gap for breathing room

          const elementPosition = element.getBoundingClientRect().top + window.scrollY;
          const offsetPosition = elementPosition - offset;

          window.scrollTo({
            top: offsetPosition,
            behavior: "smooth"
          });
        } else {
          // Desktop: normal scroll
          element.scrollIntoView({ behavior: "smooth", block: "start" });
        }

        // Detect when scrolling actually stops
        let scrollTimeout;
        const checkScrollEnd = () => {
          clearTimeout(scrollTimeout);
          scrollTimeout = setTimeout(() => {
            // Scrolling has stopped
            setIsProgrammaticScroll(false);

            // Update navbar state immediately based on final scroll position
            const finalScrollPosition = window.scrollY;
            setIsScrolled(finalScrollPosition > 20);

            // Remove listener
            window.removeEventListener('scroll', checkScrollEnd);
          }, 100);
        };

        // Listen for scroll events to detect when scrolling stops
        window.addEventListener('scroll', checkScrollEnd, { passive: true });

        // Fallback: unfreeze after max 1000ms if scroll listener doesn't fire
        setTimeout(() => {
          window.removeEventListener('scroll', checkScrollEnd);
          setIsProgrammaticScroll(false);
          const finalScrollPosition = window.scrollY;
          setIsScrolled(finalScrollPosition > 20);
        }, 1000);
      } else {
        setIsProgrammaticScroll(false);
      }
    }, 100);
  };

  const handleExploreMore = () => {
    trackCTAClick('Explore Services', 'hero_section');
    handleSmoothScroll("services");
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    // Track form submission attempt
    trackFormInteraction('submit_attempt', {
      has_message: !!e.target.message.value
    });

    try {
      const formData = new FormData();
      formData.append("access_key", "e26f217a-713f-403e-94be-7f81b8f1e932");
      formData.append(
        "name",
        `${e.target.firstName.value} ${e.target.lastName.value}`
      );
      formData.append("email", e.target.email.value);
      formData.append("message", e.target.message.value);
      formData.append(
        "subject",
        `[Zykain] New inquiry from ${e.target.firstName.value} ${e.target.lastName.value}`
      );
      formData.append("from_name", "ZYKAIN Website");

      const response = await fetch("https://api.web3forms.com/submit", {
        method: "POST",
        body: formData,
      });

      const result = await response.json();

      if (result.success) {
        // Track successful submission
        trackFormInteraction('submit_success', {
          form_name: 'contact_form'
        });

        setSubmitStatus("success");
        setShowToast(true);
        setFormData({
          firstName: "",
          lastName: "",
          email: "",
          message: "",
        });

        setTimeout(() => {
          setShowToast(false);
          setSubmitStatus(null);
        }, 5000);
      } else {
        throw new Error(result.message || "Form submission failed");
      }
    } catch (error) {
      console.error("Form submission error:", error);

      // Track form submission error
      trackFormInteraction('submit_error', {
        error_message: error.message
      });

      setSubmitStatus("error");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="bg-white font-sans">
      {/* Dynamic SEO Meta Tags */}
      <Helmet>
        <title>Zykain - Expert Jira Consulting | Workflow Automation & Optimization Services</title>
        <meta name="description" content="Zykain specializes in Jira optimization, workflow automation, custom integrations, and dashboard analytics. 14+ years of enterprise experience helping teams boost productivity by 40%. Expert Jira consultants for Cloud, Server & Data Center." />

        {/* Open Graph */}
        <meta property="og:title" content="Zykain - Expert Jira Consulting & Optimization Services" />
        <meta property="og:description" content="Transform your Jira instance with expert optimization, automation, and custom integrations. 14+ years of enterprise experience. Boost team productivity by 40%." />
        <meta property="og:url" content="https://zykain.com/" />
        <meta property="og:type" content="website" />

        {/* Twitter */}
        <meta name="twitter:title" content="Zykain - Expert Jira Consulting & Optimization Services" />
        <meta name="twitter:description" content="Transform your Jira instance with expert optimization, automation, and custom integrations. 14+ years of enterprise experience." />

        {/* Canonical URL */}
        <link rel="canonical" href="https://zykain.com/" />
      </Helmet>

      <style>
        {`
          @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800;900&display=swap');
          @import url('https://fonts.googleapis.com/css2?family=Oxanium:wght@400;500;600;700;800;900&display=swap');
          @import url('https://fonts.googleapis.com/css2?family=Poppins:wght@400;500;600;700;800;900&display=swap');
          @import url('https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@400;500;600;700&display=swap');
          @import url('https://fonts.googleapis.com/css2?family=Sora:wght@400;500;600;700;800&display=swap');

          @font-face {
            font-family: 'Machina';
            src: url('https://db.onlinewebfonts.com/t/d7e8a95865396cddca89b00080d2cba6.eot');
            src: url('https://db.onlinewebfonts.com/t/d7e8a95865396cddca89b00080d2cba6.eot?#iefix') format('embedded-opentype'),
                 url('https://db.onlinewebfonts.com/t/d7e8a95865396cddca89b00080d2cba6.woff2') format('woff2'),
                 url('https://db.onlinewebfonts.com/t/d7e8a95865396cddca89b00080d2cba6.woff') format('woff'),
                 url('https://db.onlinewebfonts.com/t/d7e8a95865396cddca89b00080d2cba6.ttf') format('truetype'),
                 url('https://db.onlinewebfonts.com/t/d7e8a95865396cddca89b00080d2cba6.svg#Machina') format('svg');
            font-weight: 900;
            font-style: normal;
          }

          * {
            font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
          }
          
          .hero-title {
            font-family: 'Poppins', sans-serif;
            font-weight: 600;
            letter-spacing: -0.02em;
            line-height: 1.1;
          }
          
          .nav-text {
            font-family: 'Avenir Light', 'Avenir', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
            font-weight: 300;
          }
          
          .logo-text {
            font-family: 'Oxanium', monospace;
            font-weight: 900;
            letter-spacing: 0.02em;
          }
          
          .content-text {
            font-family: 'Avenir Light', 'Avenir', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
            font-weight: 300;
          }
          
          .section-heading {
            font-family: 'Avenir Light', 'Avenir', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
            font-weight: 300;
          }

          .services-label {
            font-family: 'Avenir', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
            font-weight: 700;
            font-size: 16px;
          }

          .services-main-title {
            font-family: 'Work Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
            font-weight: 600;
            font-size: 36px;
            line-height: 1.2;
          }

          .service-title {
            font-family: 'Avenir', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
            font-weight: 700;
            font-size: 16px;
          }

          .service-description {
            font-family: 'Avenir Light', 'Avenir', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
            font-weight: 300;
            font-size: 18px;
            line-height: 1.4;
          }
          
          .service-card:hover {
            transform: translateY(-5px);
          }
          
          .floating-animation {
            animation: float 6s ease-in-out infinite;
          }
          
          .service-icon {
            animation: bounce-gentle 2.5s ease-in-out infinite;
          }
          
          @keyframes bounce-gentle {
            0%, 100% { transform: translateY(0px); }
            50% { transform: translateY(-8px); }
          }
          
          @keyframes float {
            0%, 100% { transform: translateY(0px); }
            50% { transform: translateY(-20px); }
          }
          
          .wave-1 {
            animation: wave-float-1 8s ease-in-out infinite;
          }
          
          .wave-2 {
            animation: wave-float-2 10s ease-in-out infinite;
          }
          
          @keyframes wave-float-1 {
            0%, 100% { transform: translateY(0px) translateX(0px); }
            33% { transform: translateY(-12px) translateX(5px); }
            66% { transform: translateY(6px) translateX(-3px); }
          }
          
          @keyframes wave-float-2 {
            0%, 100% { transform: translateY(0px) translateX(0px); }
            50% { transform: translateY(-10px) translateX(8px); }
          }
          
          .floating-dot {
            animation: float-dot 3s ease-in-out infinite;
          }
          
          .floating-dot:nth-child(odd) {
            animation-delay: -1s;
          }
          
          @keyframes float-dot {
            0%, 100% { transform: translateY(0px) translateX(0px); opacity: 0.3; }
            50% { transform: translateY(-15px) translateX(10px); opacity: 0.6; }
          }
          
          .floating-dot-wave {
            animation: float-along-wave-exact 15s linear infinite;
          }
          
          @keyframes float-along-wave-exact {
            0% {
              offset-distance: 0%;
              opacity: 0.3;
            }
            10% {
              opacity: 0.6;
            }
            90% {
              opacity: 0.6;
            }
            100% {
              offset-distance: 100%;
              opacity: 0.3;
            }
          }
          
          .grid-pattern {
            background-image: 
              linear-gradient(rgba(0, 0, 0, 0.05) 1px, transparent 1px),
              linear-gradient(90deg, rgba(0, 0, 0, 0.05) 1px, transparent 1px);
            background-size: 60px 60px;
            opacity: 0.8;
            mask: linear-gradient(to bottom, transparent 0%, transparent 94px, rgba(0,0,0,1) 158px, rgba(0,0,0,1) 100%);
            -webkit-mask: linear-gradient(to bottom, transparent 0%, transparent 94px, rgba(0,0,0,1) 158px, rgba(0,0,0,1) 100%);
          }
          
          .toast-enter {
            opacity: 0;
            transform: translateY(-20px) scale(0.95);
          }
          
          .toast-show {
            opacity: 1;
            transform: translateY(0) scale(1);
            transition: all 0.3s ease-out;
          }
          
          .toast-exit {
            opacity: 0;
            transform: translateY(-20px) scale(0.95);
            transition: all 0.3s ease-in;
          }

          .mobile-menu-overlay {
            position: fixed;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background: rgb(237, 233, 254);
            z-index: 9999;
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            animation: fadeIn 0.3s ease-out;
          }

          @keyframes fadeIn {
            from { opacity: 0; }
            to { opacity: 1; }
          }

          .mobile-menu-item {
            opacity: 0;
            transform: translateY(-30px);
            animation: slideInFromTop 0.5s ease-out forwards;
          }

          @keyframes slideInFromTop {
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }

          .hamburger-line {
            transition: all 0.3s ease-in-out;
            transform-origin: 100% 50%;
            position: absolute;
            left: 0;
            top: 50%;
          }

          .main-burger-line {
            transition: all 0.3s ease-in-out;
            position: absolute;
            left: 0;
            top: 50%;
            transform-origin: 100% 50%;
          }

          .hamburger-container {
            position: relative;
            width: 18px;
            height: 18px;
          }

          /* Add iOS-specific resets */
          .hamburger-main {
            position: relative;
            width: 18px;
            height: 18px;
            -webkit-transform: translateZ(0);
            transform: translateZ(0);
            -webkit-backface-visibility: hidden;
            backface-visibility: hidden;
          }

          /* MAIN HAMBURGER - COMPLETELY SEPARATE CSS */
          .hamburger-main .main-burger-line:nth-child(1) {
            transform: translateY(-8.48px) rotate(0deg);
            -webkit-transform: translateY(-8.48px) rotate(0deg);
          }

          .hamburger-main .main-burger-line:nth-child(2) {
            transform: translateY(0px) rotate(0deg);
            -webkit-transform: translateY(0px) rotate(0deg);
          }

          .hamburger-main .main-burger-line:nth-child(3) {
            transform: translateY(8.48px) rotate(0deg);
            -webkit-transform: translateY(8.48px) rotate(0deg);
          }

          /* CLOSE BUTTON - SEPARATE CSS */
          .hamburger-container .hamburger-line:nth-child(1) {
            transform: translateY(-8.48px);
          }

          .hamburger-container .hamburger-line:nth-child(2) {
            transform: translateY(0px);
          }

          .hamburger-container .hamburger-line:nth-child(3) {
            transform: translateY(8.48px);
          }

          .hamburger-open .hamburger-line:nth-child(1) {
            transform: translateY(-8.48px) rotate(-45deg);
            transform-origin: 100% 50%;
          }

          .hamburger-open .hamburger-line:nth-child(2) {
            opacity: 0;
            transform: scale(0);
          }

          .hamburger-open .hamburger-line:nth-child(3) {
            transform: translateY(8.48px) rotate(45deg);
            transform-origin: 100% 50%;
          }

          body.menu-open {
            overflow: hidden;
            position: fixed;
            width: 100%;
          }

          /* Wave animations */
          @keyframes wave-slow {
            0%, 100% { transform: translateX(0) translateY(10px); }
            50% { transform: translateX(-25px) translateY(10px); }
          }

          @keyframes wave-medium {
            0%, 100% { transform: translateX(0) translateY(5px); }
            50% { transform: translateX(25px) translateY(5px); }
          }

          @keyframes wave-fast {
            0%, 100% { transform: translateX(0) translateY(0); }
            50% { transform: translateX(-15px) translateY(0); }
          }

          .wave-animate-1 {
            animation: wave-slow 12s ease-in-out infinite;
          }

          .wave-animate-2 {
            animation: wave-medium 8s ease-in-out infinite;
          }

          .wave-animate-3 {
            animation: wave-fast 6s ease-in-out infinite;
          }

          @keyframes text-slide-up {
            0%, 25% {
              transform: translateY(0);
            }
            30%, 55% {
              transform: translateY(-1.4em);
            }
            60%, 85% {
              transform: translateY(-2.8em);
            }
            90%, 100% {
              transform: translateY(-4.2em);
            }
          }

          .animate-text-slide-up {
            animation: text-slide-up 6s ease-in-out infinite;
          }
        `}
      </style>

      {/* Toast Notification */}
      {showToast && submitStatus === "success" && (
        <div className="fixed top-20 left-0 right-0 flex justify-center z-50 px-4">
          <div className="bg-gradient-to-r from-indigo-600 via-violet-600 to-fuchsia-600 text-white px-6 py-4 rounded-lg shadow-lg flex items-center space-x-3 max-w-sm toast-show">
            <div className="flex-shrink-0">
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
            <div className="flex-1">
              <p className="text-sm font-medium">
                Thank you! Your message has been sent successfully.
              </p>
              <p className="text-xs opacity-90 mt-1">
                We'll get back to you soon.
              </p>
            </div>
            <button
              onClick={() => {
                setShowToast(false);
                setSubmitStatus(null);
              }}
              className="flex-shrink-0 text-white hover:text-gray-200 transition-colors"
            >
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                <path
                  fillRule="evenodd"
                  d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                  clipRule="evenodd"
                />
              </svg>
            </button>
          </div>
        </div>
      )}

      {/* Mobile Menu Overlay - Modern Redesign */}
      {mobileMenuOpen && (
        <div className="fixed inset-0 z-[9999] bg-gradient-to-br from-indigo-600 via-violet-600 to-fuchsia-600 md:hidden">
          {/* Background decoration */}
          <div className="absolute inset-0 bg-gradient-to-tr from-indigo-900/20 via-transparent to-cyan-500/10"></div>

          {/* Close button with better visibility */}
          <div className="absolute top-6 right-6 z-10">
            <button
              onClick={handleMenuClose}
              className="bg-white/20 backdrop-blur-md text-white hover:bg-white/30 p-3 rounded-full transition-all duration-300 hover:scale-110 shadow-lg"
              aria-label="Close menu"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={3}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Menu items */}
          <div className="flex flex-col items-center justify-center h-full space-y-8 px-8 relative z-0">
            <button
              onClick={() => handleSmoothScroll("home", "mobile_menu")}
              className="text-3xl text-white font-bold hover:text-cyan-300 transition-all duration-300 hover:scale-110 active:scale-95"
              style={{ fontFamily: 'Sora, sans-serif' }}
            >
              Home
            </button>
            <button
              onClick={() => handleSmoothScroll("what-we-do", "mobile_menu")}
              className="text-3xl text-white font-bold hover:text-cyan-300 transition-all duration-300 hover:scale-110 active:scale-95"
              style={{ fontFamily: 'Sora, sans-serif' }}
            >
              What We Do
            </button>
            <button
              onClick={() => handleSmoothScroll("services", "mobile_menu")}
              className="text-3xl text-white font-bold hover:text-cyan-300 transition-all duration-300 hover:scale-110 active:scale-95"
              style={{ fontFamily: 'Sora, sans-serif' }}
            >
              Services
            </button>
            <button
              onClick={() => handleSmoothScroll("how-we-work", "mobile_menu")}
              className="text-3xl text-white font-bold hover:text-cyan-300 transition-all duration-300 hover:scale-110 active:scale-95"
              style={{ fontFamily: 'Sora, sans-serif' }}
            >
              How We Work
            </button>
            <button
              onClick={() => handleSmoothScroll("faq", "mobile_menu")}
              className="text-3xl text-white font-bold hover:text-cyan-300 transition-all duration-300 hover:scale-110 active:scale-95"
              style={{ fontFamily: 'Sora, sans-serif' }}
            >
              FAQs
            </button>
            <button
              onClick={() => handleSmoothScroll("contact", "mobile_menu")}
              className="text-3xl text-white font-bold hover:text-cyan-300 transition-all duration-300 hover:scale-110 active:scale-95"
              style={{ fontFamily: 'Sora, sans-serif' }}
            >
              Contact Us
            </button>
          </div>
        </div>
      )}

      {/* Navigation - Premium Redesign */}
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled
            ? "bg-white/80 backdrop-blur-md shadow-2xl border-b border-violet-100"
            : "bg-gradient-to-b from-black/15 to-transparent backdrop-blur-sm"
        }`}
      >
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="flex justify-between items-center h-16">
            {/* Logo */}
            <div className="flex-shrink-0">
              <button
                onClick={() => handleSmoothScroll("home")}
                className="group bg-transparent border-none cursor-pointer transition-all duration-300 flex items-center gap-3"
              >
                <img
                  src={isScrolled ? "/zykain_logo.png" : "/zykain_logo_dark.png"}
                  alt="Zykain Logo"
                  className="h-8 w-auto transition-all duration-300 group-hover:scale-110"
                  onError={(e) => {
                    // Fallback to original logo if dark version doesn't exist
                    if (e.target.src.includes('dark')) {
                      e.target.src = '/zykain_logo.png';
                    }
                  }}
                />
                <span
                  className={`text-2xl font-black tracking-tight transition-all duration-300 ${
                    isScrolled ? "text-gray-900" : "text-white"
                  }`}
                  style={{ fontFamily: 'Sora, sans-serif', fontWeight: '900' }}
                >
                  Zykain
                </span>
              </button>
            </div>

            {/* Desktop Navigation */}
            <div className="hidden md:block ml-auto">
              <div className="flex items-center space-x-1">
                <button
                  onClick={() => handleSmoothScroll("home")}
                  className={`px-4 py-2 rounded-lg font-medium text-sm tracking-wide transition-all duration-300 relative group ${
                    isScrolled
                      ? "text-gray-900 hover:bg-gray-100"
                      : "text-white hover:text-white"
                  }`}
                  style={{ fontFamily: 'Inter, sans-serif', fontWeight: '500' }}
                >
                  <span className="relative z-10">Home</span>
                  {!isScrolled && <div className="absolute inset-0 bg-white/10 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity"></div>}
                </button>
                <button
                  onClick={() => handleSmoothScroll("what-we-do")}
                  className={`px-4 py-2 rounded-lg font-medium text-sm tracking-wide transition-all duration-300 relative group ${
                    isScrolled
                      ? "text-gray-900 hover:bg-gray-100"
                      : "text-white hover:text-white"
                  }`}
                  style={{ fontFamily: 'Inter, sans-serif', fontWeight: '500' }}
                >
                  <span className="relative z-10">What We Do</span>
                  {!isScrolled && <div className="absolute inset-0 bg-white/10 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity"></div>}
                </button>
                <button
                  onClick={() => handleSmoothScroll("services")}
                  className={`px-4 py-2 rounded-lg font-medium text-sm tracking-wide transition-all duration-300 relative group ${
                    isScrolled
                      ? "text-gray-900 hover:bg-gray-100"
                      : "text-white hover:text-white"
                  }`}
                  style={{ fontFamily: 'Inter, sans-serif', fontWeight: '500' }}
                >
                  <span className="relative z-10">Services</span>
                  {!isScrolled && <div className="absolute inset-0 bg-white/10 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity"></div>}
                </button>
                <button
                  onClick={() => handleSmoothScroll("how-we-work")}
                  className={`px-4 py-2 rounded-lg font-medium text-sm tracking-wide transition-all duration-300 relative group ${
                    isScrolled
                      ? "text-gray-900 hover:bg-gray-100"
                      : "text-white hover:text-white"
                  }`}
                  style={{ fontFamily: 'Inter, sans-serif', fontWeight: '500' }}
                >
                  <span className="relative z-10">How We Work</span>
                  {!isScrolled && <div className="absolute inset-0 bg-white/10 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity"></div>}
                </button>
                <button
                  onClick={() => handleSmoothScroll("faq")}
                  className={`px-4 py-2 rounded-lg font-medium text-sm tracking-wide transition-all duration-300 relative group ${
                    isScrolled
                      ? "text-gray-900 hover:bg-gray-100"
                      : "text-white hover:text-white"
                  }`}
                  style={{ fontFamily: 'Inter, sans-serif', fontWeight: '500' }}
                >
                  <span className="relative z-10">FAQs</span>
                  {!isScrolled && <div className="absolute inset-0 bg-white/10 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity"></div>}
                </button>
                <button
                  onClick={() => handleSmoothScroll("contact")}
                  className={`px-4 py-2 rounded-lg font-medium text-sm tracking-wide transition-all duration-300 relative group ${
                    isScrolled
                      ? "text-gray-900 hover:bg-gray-100"
                      : "text-white hover:text-white"
                  }`}
                  style={{ fontFamily: 'Inter, sans-serif', fontWeight: '500' }}
                >
                  <span className="relative z-10">Contact Us</span>
                  {!isScrolled && <div className="absolute inset-0 bg-white/10 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity"></div>}
                </button>
              </div>
            </div>


            {/* Mobile menu button */}
            <div className="md:hidden">
              <button
                onClick={() => setMobileMenuOpen(true)}
                className={`p-2 bg-transparent border-none cursor-pointer transition-colors ${
                  isScrolled ? "text-gray-600 hover:text-gray-800" : "text-white hover:text-white/80"
                }`}
                aria-label="Open mobile menu"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section - Enhanced Design with Waves */}
      <section
        id="home"
        className="relative bg-gradient-to-br from-indigo-600 via-violet-600 to-fuchsia-600 overflow-hidden min-h-screen"
      >
        {/* Animated gradient overlay */}
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-cyan-400/20 via-transparent to-transparent pointer-events-none"></div>


        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col justify-center min-h-screen py-[80px] md:min-h-0 md:flex-none md:pt-24 lg:pt-32 md:pb-24 lg:pb-32 relative z-10">
          <div className="text-center relative z-10 -mt-[60px] md:mt-0">
            {/* Badge */}
            <div className="inline-flex items-center gap-2 px-5 py-2.5 bg-white/15 backdrop-blur-md rounded-full border border-white/30 mb-6 shadow-xl">
              <div className="w-2.5 h-2.5 bg-emerald-400 rounded-full animate-pulse shadow-lg shadow-emerald-400/50"></div>
              <span className="text-white text-sm font-semibold flex items-center gap-1.5" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                Trusted by Growing
                <span className="inline-block overflow-hidden h-[1.4em] align-bottom relative text-left" style={{ width: '6.5em' }}>
                  <span className="absolute left-0 animate-text-slide-up">
                    <span className="block h-[1.4em] leading-[1.4em] text-yellow-300">Tech Teams</span>
                    <span className="block h-[1.4em] leading-[1.4em] text-yellow-300">Startups</span>
                    <span className="block h-[1.4em] leading-[1.4em] text-yellow-300">Enterprise</span>
                    <span className="block h-[1.4em] leading-[1.4em] text-yellow-300">Tech Teams</span>
                  </span>
                </span>
              </span>
            </div>

            {/* Main Title */}
            <h1 className="text-5xl md:text-7xl lg:text-8xl font-extrabold text-white mb-6 leading-[1.1] tracking-tight" style={{ fontFamily: 'Sora, sans-serif' }}>
              Transform Jira
              <br className="md:hidden" />
              <span className="md:inline"> Chaos</span>
              <br className="hidden md:block" />
              <span className="md:inline"> Into</span>
              <br className="md:hidden" />
              {" "}
              <span className="bg-gradient-to-r from-cyan-300 via-fuchsia-300 to-orange-300 bg-clip-text text-transparent animate-gradient">
                Team Clarity
              </span>
            </h1>

            {/* Subtitle */}
            <p className="text-xl md:text-2xl text-indigo-100 mb-8 max-w-3xl mx-auto leading-relaxed" style={{ fontFamily: 'Space Grotesk, sans-serif', fontWeight: 400 }}>
              We help tech teams unlock Jira's full potential through intelligent automation,
              streamlined workflows, and data-driven dashboards.
            </p>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-3 md:gap-4 justify-center items-center -mt-2 mb-4 md:mt-0 md:mb-6">
              <button
                onClick={() => {
                  trackCTAClick('Book Free Consultation', 'hero_section');
                  handleSmoothScroll("contact");
                }}
                className="group relative bg-white text-violet-700 px-6 py-3 md:px-8 md:py-4 rounded-full md:rounded-2xl text-base md:text-lg font-bold hover:bg-violet-50 transition-all duration-300 shadow-2xl hover:shadow-violet-400/50 hover:scale-105"
                style={{ fontFamily: 'Sora, sans-serif' }}
              >
                <span className="relative z-10">Book Free Consultation</span>
                <div className="absolute inset-0 bg-gradient-to-r from-cyan-400 to-fuchsia-500 rounded-full md:rounded-2xl opacity-0 group-hover:opacity-10 transition-opacity"></div>
              </button>
              <button
                onClick={handleExploreMore}
                className="bg-white/10 backdrop-blur-md text-white border-2 border-white/40 px-6 py-3 md:px-8 md:py-4 rounded-full md:rounded-2xl text-base md:text-lg font-bold hover:bg-white/20 hover:border-white/60 transition-all duration-300 hover:scale-105 shadow-xl"
                style={{ fontFamily: 'Sora, sans-serif' }}
              >
                Explore Services
              </button>
            </div>

            {/* Trust indicators */}
            <div className="hidden md:flex flex-wrap justify-center gap-8 items-center text-white/90 mb-8 mt-12" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full">
                <svg className="w-5 h-5 text-emerald-400" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                <span className="text-sm font-semibold">14+ Years Experience</span>
              </div>
              <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full">
                <svg className="w-5 h-5 text-emerald-400" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                <span className="text-sm font-semibold">50+ Automations Built</span>
              </div>
              <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full">
                <svg className="w-5 h-5 text-emerald-400" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                <span className="text-sm font-semibold">60% Time Saved</span>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom wave decoration - Traditional layered waves */}
        <div className="absolute bottom-[60px] md:bottom-0 left-0 right-0 z-0 overflow-hidden">
          {/* Wave Layer 1 - Back wave */}
          <svg viewBox="0 0 1440 320" className="absolute bottom-0 wave-animate-1" preserveAspectRatio="none" style={{ height: '200px', width: '120%', left: '-10%' }}>
            <defs>
              <linearGradient id="wave-gradient-1" x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" style={{ stopColor: '#e0e7ff', stopOpacity: 0.3 }} />
                <stop offset="50%" style={{ stopColor: '#ddd6fe', stopOpacity: 0.3 }} />
                <stop offset="100%" style={{ stopColor: '#fae8ff', stopOpacity: 0.3 }} />
              </linearGradient>
            </defs>
            <path d="M0,96 C120,120 240,160 360,160 C480,160 600,128 720,128 C840,128 960,160 1080,160 C1200,160 1320,128 1380,112 L1440,96 L1440,320 L0,320 Z" fill="url(#wave-gradient-1)"/>
          </svg>

          {/* Wave Layer 2 - Middle wave */}
          <svg viewBox="0 0 1440 320" className="absolute bottom-0 wave-animate-2" preserveAspectRatio="none" style={{ height: '180px', width: '120%', left: '-10%' }}>
            <defs>
              <linearGradient id="wave-gradient-2" x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" style={{ stopColor: '#e0e7ff', stopOpacity: 0.5 }} />
                <stop offset="50%" style={{ stopColor: '#ddd6fe', stopOpacity: 0.5 }} />
                <stop offset="100%" style={{ stopColor: '#fae8ff', stopOpacity: 0.5 }} />
              </linearGradient>
            </defs>
            <path d="M0,160 C80,140 160,96 240,96 C320,96 400,140 480,160 C560,180 640,192 720,192 C800,192 880,160 960,140 C1040,120 1120,96 1200,96 C1280,96 1360,128 1400,144 L1440,160 L1440,320 L0,320 Z" fill="url(#wave-gradient-2)"/>
          </svg>

          {/* Wave Layer 3 - Front wave */}
          <svg viewBox="0 0 1440 320" className="relative wave-animate-3" preserveAspectRatio="none" style={{ height: '160px', width: '120%', left: '-10%' }}>
            <defs>
              <linearGradient id="wave-gradient-3" x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" style={{ stopColor: '#dbeafe', stopOpacity: 1 }} />
                <stop offset="50%" style={{ stopColor: '#f0abfc', stopOpacity: 1 }} />
                <stop offset="100%" style={{ stopColor: '#e879f9', stopOpacity: 1 }} />
              </linearGradient>
            </defs>
            <path d="M0,224 C60,240 120,256 180,256 C240,256 300,240 360,224 C420,208 480,192 540,192 C600,192 660,208 720,224 C780,240 840,256 900,256 C960,256 1020,240 1080,224 C1140,208 1200,192 1260,192 C1320,192 1380,208 1410,216 L1440,224 L1440,320 L0,320 Z" fill="url(#wave-gradient-3)"/>
          </svg>
        </div>
      </section>

      {/* Problem Section - Premium Redesign */}
      <section id="what-we-do" className="pt-12 pb-24 lg:pt-20 lg:pb-40 bg-gradient-to-br from-fuchsia-600 via-violet-600 to-indigo-600 relative overflow-hidden scroll-mt-[20px]">
        {/* Animated gradient overlay */}
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_left,_var(--tw-gradient-stops))] from-cyan-400/20 via-transparent to-transparent pointer-events-none"></div>

        {/* Background decoration */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-gradient-to-bl from-orange-400/20 via-pink-400/10 to-transparent rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-gradient-to-tr from-blue-400/20 to-transparent rounded-full blur-3xl"></div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center mb-10">
            <div className="inline-flex items-center gap-2 px-6 py-3 bg-white/20 backdrop-blur-md rounded-full border border-white/30 shadow-2xl mb-8">
              <svg className="w-4 h-4 text-yellow-300" fill="currentColor" viewBox="0 0 20 20">
                <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
              </svg>
              <span className="font-bold tracking-wide text-yellow-300" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>WHAT WE DO</span>
            </div>
            <h2 className="text-5xl lg:text-7xl font-black text-white mb-6 leading-tight" style={{ fontFamily: 'Sora, sans-serif' }}>
              From Jira <span className="bg-gradient-to-r from-orange-300 via-yellow-200 to-orange-300 bg-clip-text text-transparent">Pain Points</span> to<br/>
              <span className="bg-gradient-to-r from-cyan-300 via-blue-200 to-cyan-300 bg-clip-text text-transparent">Peak Performance</span>
            </h2>
            <p className="text-2xl text-indigo-100 max-w-3xl mx-auto leading-relaxed" style={{ fontFamily: 'Space Grotesk, sans-serif', fontWeight: 400 }}>
              We turn your biggest frustrations into competitive advantages
            </p>
          </div>

          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 lg:gap-10 max-w-[359px] mx-auto md:max-w-none">
            {/* Card 1 */}
            <div className="group relative rounded-3xl transition-all duration-300 hover:shadow-2xl hover:-translate-y-2 overflow-hidden bg-white/10 backdrop-blur-xl border border-white/20">
              <div className="p-5 md:p-8">
                {/* Icon */}
                <div className="mb-4 md:mb-6 flex justify-center">
                  <div className="w-14 h-14 rounded-2xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                    <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                </div>

                {/* Title */}
                <h3 className="text-xl font-bold text-white mb-3 text-center" style={{ fontFamily: 'Inter, sans-serif' }}>
                  Setup Nightmare
                </h3>

                {/* Problem Description */}
                <p className="text-white/90 mb-4 md:mb-6 leading-relaxed text-center text-sm">
                  Setup takes weeks, not hours. Complex configurations slow you down.
                </p>

                {/* Divider */}
                <div className="h-px mb-4 md:mb-6 bg-white/20"></div>

                {/* Solution Section */}
                <div className="space-y-3">
                  <h4 className="text-white font-semibold text-sm text-left">Our Solution:</h4>
                  <p className="text-white/80 text-sm leading-relaxed text-left">
                    Running in days with battle-tested templates
                  </p>
                </div>
              </div>
            </div>

            {/* Card 2 */}
            <div className="group relative rounded-3xl transition-all duration-300 hover:shadow-2xl hover:-translate-y-2 overflow-hidden bg-white/10 backdrop-blur-xl border border-white/20">
              <div className="p-5 md:p-8">
                {/* Icon */}
                <div className="mb-4 md:mb-6 flex justify-center">
                  <div className="w-14 h-14 rounded-2xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                    <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                    </svg>
                  </div>
                </div>

                {/* Title */}
                <h3 className="text-xl font-bold text-white mb-3 text-center" style={{ fontFamily: 'Inter, sans-serif' }}>
                  Manual Madness
                </h3>

                {/* Problem Description */}
                <p className="text-white/90 mb-4 md:mb-6 leading-relaxed text-center text-sm">
                  10+ hours weekly on manual reports. Data entry wastes precious time.
                </p>

                {/* Divider */}
                <div className="h-px mb-4 md:mb-6 bg-white/20"></div>

                {/* Solution Section */}
                <div className="space-y-3">
                  <h4 className="text-white font-semibold text-sm text-left">Our Solution:</h4>
                  <p className="text-white/80 text-sm leading-relaxed text-left">
                    Automated dashboards with real-time Slack updates
                  </p>
                </div>
              </div>
            </div>

            {/* Card 3 */}
            <div className="group relative rounded-3xl transition-all duration-300 hover:shadow-2xl hover:-translate-y-2 overflow-hidden bg-white/10 backdrop-blur-xl border border-white/20">
              <div className="p-5 md:p-8">
                {/* Icon */}
                <div className="mb-4 md:mb-6 flex justify-center">
                  <div className="w-14 h-14 rounded-2xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                    <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                    </svg>
                  </div>
                </div>

                {/* Title */}
                <h3 className="text-xl font-bold text-white mb-3 text-center" style={{ fontFamily: 'Inter, sans-serif' }}>
                  Visibility Black Hole
                </h3>

                {/* Problem Description */}
                <p className="text-white/90 mb-4 md:mb-6 leading-relaxed text-center text-sm">
                  No clue who has capacity. Invisible workload causes burnout.
                </p>

                {/* Divider */}
                <div className="h-px mb-4 md:mb-6 bg-white/20"></div>

                {/* Solution Section */}
                <div className="space-y-3">
                  <h4 className="text-white font-semibold text-sm text-left">Our Solution:</h4>
                  <p className="text-white/80 text-sm leading-relaxed text-left">
                    Crystal-clear resource allocation dashboards
                  </p>
                </div>
              </div>
            </div>
          </div>
          </div>
        </div>
      </section>

      {/* Services Section - Premium Redesign */}
      <section
        id="services"
        className="pt-12 pb-24 lg:pt-20 lg:pb-40 bg-gradient-to-b from-white via-gray-50 to-white relative overflow-hidden scroll-mt-[20px]"
      >
        {/* Background decoration */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-1/4 left-0 w-[500px] h-[500px] bg-gradient-to-br from-indigo-200/30 via-violet-200/20 to-transparent rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 right-0 w-[600px] h-[600px] bg-gradient-to-tl from-fuchsia-200/30 via-violet-200/20 to-transparent rounded-full blur-3xl"></div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center mb-10">
            <div className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-indigo-600 via-violet-600 to-fuchsia-600 text-white rounded-full shadow-2xl mb-8 shadow-violet-500/30">
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                <path d="M10.394 2.08a1 1 0 00-.788 0l-7 3a1 1 0 000 1.84L5.25 8.051a.999.999 0 01.356-.257l4-1.714a1 1 0 11.788 1.838L7.667 9.088l1.94.831a1 1 0 00.787 0l7-3a1 1 0 000-1.838l-7-3zM3.31 9.397L5 10.12v4.102a8.969 8.969 0 00-1.05-.174 1 1 0 01-.89-.89 11.115 11.115 0 01.25-3.762zM9.3 16.573A9.026 9.026 0 007 14.935v-3.957l1.818.78a3 3 0 002.364 0l5.508-2.361a11.026 11.026 0 01.25 3.762 1 1 0 01-.89.89 8.968 8.968 0 00-5.35 2.524 1 1 0 01-1.4 0zM6 18a1 1 0 001-1v-2.065a8.935 8.935 0 00-2-.712V17a1 1 0 001 1z"/>
              </svg>
              <span className="font-bold tracking-wide" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>OUR SERVICES</span>
            </div>
            <h2 className="text-5xl lg:text-7xl font-black text-gray-900 mb-6 leading-tight" style={{ fontFamily: 'Sora, sans-serif' }}>
              Jira Optimization<br/>
              <span className="bg-gradient-to-r from-indigo-600 via-violet-600 to-fuchsia-600 bg-clip-text text-transparent">That Delivers</span>
            </h2>
            <p className="text-2xl text-gray-600 max-w-3xl mx-auto leading-relaxed" style={{ fontFamily: 'Space Grotesk, sans-serif', fontWeight: 400 }}>
              Choose the perfect package to transform your Jira workflow
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 max-w-[359px] mx-auto md:max-w-none">
            {/* Service 1: Jira Health Audit */}
            <div className="group relative rounded-3xl transition-all duration-300 hover:shadow-2xl hover:-translate-y-2 overflow-hidden bg-gradient-to-br from-indigo-500/10 via-violet-500/10 to-fuchsia-500/10 backdrop-blur-xl border border-indigo-200/30">
              <div className="p-5 md:p-8 h-full flex flex-col">
                {/* Icon */}
                <div className="mb-4 md:mb-6 flex justify-center h-14">
                  <svg className="w-12 h-12 text-indigo-600 group-hover:scale-110 transition-transform duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </div>

                {/* Title */}
                <h4 className="text-xl font-bold text-gray-900 mb-2 text-center" style={{ fontFamily: 'Inter, sans-serif' }}>
                  Jira Instance Review<br/>& Health Audit
                </h4>

                {/* Duration */}
                <div className="flex items-center justify-center gap-2 text-xs text-gray-600 mb-4">
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd"/>
                  </svg>
                  <span className="font-semibold">1-2 weeks</span>
                </div>

                {/* Description */}
                <p className="text-gray-700 mb-4 md:mb-6 leading-relaxed text-center text-sm">
                  Deep-dive analysis of your Jira setup with actionable roadmap
                </p>

                {/* Divider */}
                <div className="h-px mb-4 md:mb-6 bg-gradient-to-r from-transparent via-indigo-300 to-transparent"></div>

                {/* Features Section */}
                <div className="space-y-3 mb-4 md:mb-6 flex-grow">
                  <div className="flex items-start gap-2">
                    <svg className="w-5 h-5 md:w-4 md:h-4 text-indigo-600 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    <span className="text-sm md:text-xs text-gray-700">Complete assessment</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <svg className="w-5 h-5 md:w-4 md:h-4 text-indigo-600 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    <span className="text-sm md:text-xs text-gray-700">Improvement roadmap</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <svg className="w-5 h-5 md:w-4 md:h-4 text-indigo-600 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    <span className="text-sm md:text-xs text-gray-700">Executive presentation</span>
                  </div>
                </div>

                <button
                  onClick={() => {
                    trackServiceInteraction('Jira Instance Review & Health Audit', 'get_started_click');
                    handleSmoothScroll("contact");
                  }}
                  className="w-full bg-gradient-to-r from-indigo-600 via-violet-600 to-fuchsia-600 text-white py-3.5 rounded-2xl font-bold hover:shadow-xl hover:shadow-violet-500/50 transition-all duration-300 text-sm hover:scale-105"
                  style={{ fontFamily: 'Sora, sans-serif' }}
                >
                  Get Started →
                </button>
              </div>
            </div>

            {/* Service 2: Workflow & Automation */}
            <div className="group relative rounded-3xl transition-all duration-300 hover:shadow-2xl hover:-translate-y-2 overflow-hidden bg-gradient-to-br from-cyan-500/10 via-indigo-500/10 to-violet-500/10 backdrop-blur-xl border border-cyan-200/30">
              <div className="p-5 md:p-8 h-full flex flex-col">
                {/* Icon */}
                <div className="mb-4 md:mb-6 flex justify-center h-14">
                  <svg className="w-12 h-12 text-cyan-600 group-hover:scale-110 transition-transform duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                </div>

                {/* Title */}
                <h4 className="text-xl font-bold text-gray-900 mb-2 text-center" style={{ fontFamily: 'Inter, sans-serif' }}>
                  Workflow & Automation
                </h4>

                {/* Duration */}
                <div className="flex items-center justify-center gap-2 text-xs text-gray-600 mb-4">
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd"/>
                  </svg>
                  <span className="font-semibold">2-3 weeks</span>
                </div>

                {/* Description */}
                <p className="text-gray-700 mb-4 md:mb-6 leading-relaxed text-center text-sm">
                  Custom workflows & intelligent automation rules
                </p>

                {/* Divider */}
                <div className="h-px mb-4 md:mb-6 bg-gradient-to-r from-transparent via-cyan-300 to-transparent"></div>

                {/* Features Section */}
                <div className="space-y-3 mb-4 md:mb-6 flex-grow">
                  <div className="flex items-start gap-2">
                    <svg className="w-5 h-5 md:w-4 md:h-4 text-cyan-600 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    <span className="text-sm md:text-xs text-gray-700">Tailored workflows</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <svg className="w-5 h-5 md:w-4 md:h-4 text-cyan-600 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    <span className="text-sm md:text-xs text-gray-700">10+ automation rules</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <svg className="w-5 h-5 md:w-4 md:h-4 text-cyan-600 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    <span className="text-sm md:text-xs text-gray-700">Team training</span>
                  </div>
                </div>

                <button
                  onClick={() => {
                    trackServiceInteraction('Workflow & Automation', 'get_started_click');
                    handleSmoothScroll("contact");
                  }}
                  className="w-full bg-gradient-to-r from-indigo-600 via-violet-600 to-fuchsia-600 text-white py-3.5 rounded-2xl font-bold hover:shadow-xl hover:shadow-violet-500/50 transition-all duration-300 text-sm hover:scale-105"
                  style={{ fontFamily: 'Sora, sans-serif' }}
                >
                  Get Started →
                </button>
              </div>
            </div>

            {/* Service 3: Dashboard & Analytics */}
            <div className="group relative rounded-3xl transition-all duration-300 hover:shadow-2xl hover:-translate-y-2 overflow-hidden bg-gradient-to-br from-emerald-500/10 via-cyan-500/10 to-indigo-500/10 backdrop-blur-xl border border-emerald-200/30">
              <div className="p-5 md:p-8 h-full flex flex-col">
                {/* Icon */}
                <div className="mb-4 md:mb-6 flex justify-center h-14">
                  <svg className="w-12 h-12 text-emerald-600 group-hover:scale-110 transition-transform duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                  </svg>
                </div>

                {/* Title */}
                <h4 className="text-xl font-bold text-gray-900 mb-2 text-center" style={{ fontFamily: 'Inter, sans-serif' }}>
                  Dashboard & Analytics
                </h4>

                {/* Duration */}
                <div className="flex items-center justify-center gap-2 text-xs text-gray-600 mb-4">
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd"/>
                  </svg>
                  <span className="font-semibold">2-3 weeks</span>
                </div>

                {/* Description */}
                <p className="text-gray-700 mb-4 md:mb-6 leading-relaxed text-center text-sm">
                  Real-time dashboards & performance metrics
                </p>

                {/* Divider */}
                <div className="h-px mb-4 md:mb-6 bg-gradient-to-r from-transparent via-emerald-300 to-transparent"></div>

                {/* Features Section */}
                <div className="space-y-3 mb-4 md:mb-6 flex-grow">
                  <div className="flex items-start gap-2">
                    <svg className="w-5 h-5 md:w-4 md:h-4 text-emerald-600 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    <span className="text-sm md:text-xs text-gray-700">5-7 custom dashboards</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <svg className="w-5 h-5 md:w-4 md:h-4 text-emerald-600 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    <span className="text-sm md:text-xs text-gray-700">Real-time tracking</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <svg className="w-5 h-5 md:w-4 md:h-4 text-emerald-600 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    <span className="text-sm md:text-xs text-gray-700">Self-service filters</span>
                  </div>
                </div>

                <button
                  onClick={() => {
                    trackServiceInteraction('Dashboard & Analytics', 'get_started_click');
                    handleSmoothScroll("contact");
                  }}
                  className="w-full bg-gradient-to-r from-indigo-600 via-violet-600 to-fuchsia-600 text-white py-3.5 rounded-2xl font-bold hover:shadow-xl hover:shadow-violet-500/50 transition-all duration-300 text-sm hover:scale-105"
                  style={{ fontFamily: 'Sora, sans-serif' }}
                >
                  Get Started →
                </button>
              </div>
            </div>

            {/* Service 4: Integration & Custom */}
            <div className="group relative rounded-3xl transition-all duration-300 hover:shadow-2xl hover:-translate-y-2 overflow-hidden bg-gradient-to-br from-fuchsia-500/10 via-orange-500/10 to-rose-500/10 backdrop-blur-xl border border-fuchsia-200/30">
              <div className="p-5 md:p-8 h-full flex flex-col">
                {/* Icon */}
                <div className="mb-4 md:mb-6 flex justify-center h-14">
                  <svg className="w-12 h-12 text-fuchsia-600 group-hover:scale-110 transition-transform duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                  </svg>
                </div>

                {/* Title */}
                <h4 className="text-xl font-bold text-gray-900 mb-2 text-center" style={{ fontFamily: 'Inter, sans-serif' }}>
                  Integration & Custom Development
                </h4>

                {/* Duration */}
                <div className="flex items-center justify-center gap-2 text-xs text-gray-600 mb-4">
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd"/>
                  </svg>
                  <span className="font-semibold">3-4 weeks</span>
                </div>

                {/* Description */}
                <p className="text-gray-700 mb-4 md:mb-6 leading-relaxed text-center text-sm">
                  Advanced integrations & migrations
                </p>

                {/* Divider */}
                <div className="h-px mb-4 md:mb-6 bg-gradient-to-r from-transparent via-fuchsia-300 to-transparent"></div>

                {/* Features Section */}
                <div className="space-y-3 mb-4 md:mb-6 flex-grow">
                  <div className="flex items-start gap-2">
                    <svg className="w-5 h-5 md:w-4 md:h-4 text-fuchsia-600 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    <span className="text-sm md:text-xs text-gray-700">Custom integrations</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <svg className="w-5 h-5 md:w-4 md:h-4 text-fuchsia-600 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    <span className="text-sm md:text-xs text-gray-700">Tool migrations</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <svg className="w-5 h-5 md:w-4 md:h-4 text-fuchsia-600 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    <span className="text-sm md:text-xs text-gray-700">API solutions</span>
                  </div>
                </div>

                <button
                  onClick={() => {
                    trackServiceInteraction('Integration & Custom Development', 'get_started_click');
                    handleSmoothScroll("contact");
                  }}
                  className="w-full bg-gradient-to-r from-indigo-600 via-violet-600 to-fuchsia-600 text-white py-3.5 rounded-2xl font-bold hover:shadow-xl hover:shadow-violet-500/50 transition-all duration-300 text-sm hover:scale-105"
                  style={{ fontFamily: 'Sora, sans-serif' }}
                >
                  Get Started →
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section id="how-we-work" className="pt-12 pb-24 lg:pt-20 lg:pb-40 bg-gray-50 scroll-mt-[20px]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <div className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-indigo-100/80 via-violet-100/80 to-fuchsia-100/80 backdrop-blur-md rounded-full border border-indigo-200/50 shadow-xl mb-8">
              <svg className="w-4 h-4 text-indigo-600" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M11.3 1.046A1 1 0 0112 2v5h4a1 1 0 01.82 1.573l-7 10A1 1 0 018 18v-5H4a1 1 0 01-.82-1.573l7-10a1 1 0 011.12-.38z" clipRule="evenodd" />
              </svg>
              <span className="font-bold tracking-wide text-indigo-700" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>HOW WE WORK</span>
            </div>
            <h2 className="text-5xl lg:text-7xl font-black text-gray-900 mb-6 leading-tight" style={{ fontFamily: 'Sora, sans-serif' }}>
              Simple, <span className="bg-gradient-to-r from-indigo-600 via-violet-600 to-fuchsia-600 bg-clip-text text-transparent">Proven</span> Process
            </h2>
            <p className="text-2xl text-gray-600 max-w-3xl mx-auto leading-relaxed" style={{ fontFamily: 'Space Grotesk, sans-serif', fontWeight: 400 }}>
              From discovery to delivery, we make it seamless
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8 max-w-[359px] mx-auto md:max-w-none">
            {/* Step 1 */}
            <div className="group relative rounded-3xl transition-all duration-300 hover:shadow-2xl hover:-translate-y-2 overflow-hidden bg-gradient-to-br from-indigo-500/10 via-violet-500/10 to-indigo-500/5 backdrop-blur-xl border border-indigo-200/40">
              <div className="p-5 md:p-6 h-full flex flex-col">
                {/* Number Badge */}
                <div className="relative mb-4 md:mb-6">
                  <div className="w-14 h-14 bg-gradient-to-br from-indigo-600 to-violet-600 rounded-full flex items-center justify-center text-white text-2xl font-black mx-auto shadow-xl group-hover:scale-110 transition-transform duration-300">
                    1
                  </div>
                </div>

                {/* Title */}
                <h4 className="text-xl font-bold text-gray-900 mb-2 text-center" style={{ fontFamily: 'Sora, sans-serif' }}>
                  Discovery Call
                </h4>

                {/* Subtitle */}
                <p className="text-xs text-indigo-600 font-semibold mb-4 md:mb-5 text-center">30 minutes, free</p>

                {/* Divider */}
                <div className="h-px mb-4 md:mb-5 bg-gradient-to-r from-transparent via-indigo-300 to-transparent"></div>

                {/* Features */}
                <div className="space-y-3 flex-grow">
                  <div className="flex items-start gap-2">
                    <svg className="w-5 h-5 md:w-4 md:h-4 text-indigo-600 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    <span className="text-sm text-gray-700">Understand your challenges</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <svg className="w-5 h-5 md:w-4 md:h-4 text-indigo-600 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    <span className="text-sm text-gray-700">Assess current Jira setup</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <svg className="w-5 h-5 md:w-4 md:h-4 text-indigo-600 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    <span className="text-sm text-gray-700">Recommend best approach</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <svg className="w-5 h-5 md:w-4 md:h-4 text-indigo-600 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    <span className="text-sm text-gray-700">No obligation</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Step 2 */}
            <div className="group relative rounded-3xl transition-all duration-300 hover:shadow-2xl hover:-translate-y-2 overflow-hidden bg-gradient-to-br from-violet-500/10 via-fuchsia-500/10 to-violet-500/5 backdrop-blur-xl border border-violet-200/40">
              <div className="p-5 md:p-6 h-full flex flex-col">
                {/* Number Badge */}
                <div className="relative mb-4 md:mb-6">
                  <div className="w-14 h-14 bg-gradient-to-br from-violet-600 to-fuchsia-600 rounded-full flex items-center justify-center text-white text-2xl font-black mx-auto shadow-xl group-hover:scale-110 transition-transform duration-300">
                    2
                  </div>
                </div>

                {/* Title */}
                <h4 className="text-xl font-bold text-gray-900 mb-2 text-center" style={{ fontFamily: 'Sora, sans-serif' }}>
                  Proposal
                </h4>

                {/* Subtitle */}
                <p className="text-xs text-violet-600 font-semibold mb-4 md:mb-5 text-center">Delivered within 48 hours</p>

                {/* Divider */}
                <div className="h-px mb-4 md:mb-5 bg-gradient-to-r from-transparent via-violet-300 to-transparent"></div>

                {/* Features */}
                <div className="space-y-3 flex-grow">
                  <div className="flex items-start gap-2">
                    <svg className="w-5 h-5 md:w-4 md:h-4 text-violet-600 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    <span className="text-sm text-gray-700">Clear scope and timeline</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <svg className="w-5 h-5 md:w-4 md:h-4 text-violet-600 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    <span className="text-sm text-gray-700">Fixed pricing, no surprises</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <svg className="w-5 h-5 md:w-4 md:h-4 text-violet-600 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    <span className="text-sm text-gray-700">Detailed deliverables</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <svg className="w-5 h-5 md:w-4 md:h-4 text-violet-600 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    <span className="text-sm text-gray-700">Flexible payment terms</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Step 3 */}
            <div className="group relative rounded-3xl transition-all duration-300 hover:shadow-2xl hover:-translate-y-2 overflow-hidden bg-gradient-to-br from-fuchsia-500/10 via-pink-500/10 to-fuchsia-500/5 backdrop-blur-xl border border-fuchsia-200/40">
              <div className="p-5 md:p-6 h-full flex flex-col">
                {/* Number Badge */}
                <div className="relative mb-4 md:mb-6">
                  <div className="w-14 h-14 bg-gradient-to-br from-fuchsia-600 to-pink-600 rounded-full flex items-center justify-center text-white text-2xl font-black mx-auto shadow-xl group-hover:scale-110 transition-transform duration-300">
                    3
                  </div>
                </div>

                {/* Title */}
                <h4 className="text-xl font-bold text-gray-900 mb-2 text-center" style={{ fontFamily: 'Sora, sans-serif' }}>
                  Implementation
                </h4>

                {/* Subtitle */}
                <p className="text-xs text-fuchsia-600 font-semibold mb-4 md:mb-5 text-center">1-3 weeks typically</p>

                {/* Divider */}
                <div className="h-px mb-4 md:mb-5 bg-gradient-to-r from-transparent via-fuchsia-300 to-transparent"></div>

                {/* Features */}
                <div className="space-y-3 flex-grow">
                  <div className="flex items-start gap-2">
                    <svg className="w-5 h-5 md:w-4 md:h-4 text-fuchsia-600 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    <span className="text-sm text-gray-700">Instance setup and configuration</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <svg className="w-5 h-5 md:w-4 md:h-4 text-fuchsia-600 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    <span className="text-sm text-gray-700">Regular progress check-ins</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <svg className="w-5 h-5 md:w-4 md:h-4 text-fuchsia-600 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    <span className="text-sm text-gray-700">Your team involved throughout</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <svg className="w-5 h-5 md:w-4 md:h-4 text-fuchsia-600 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    <span className="text-sm text-gray-700">Transparent communication</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Step 4 */}
            <div className="group relative rounded-3xl transition-all duration-300 hover:shadow-2xl hover:-translate-y-2 overflow-hidden bg-gradient-to-br from-cyan-500/10 via-indigo-500/10 to-cyan-500/5 backdrop-blur-xl border border-cyan-200/40">
              <div className="p-5 md:p-6 h-full flex flex-col">
                {/* Number Badge */}
                <div className="relative mb-4 md:mb-6">
                  <div className="w-14 h-14 bg-gradient-to-br from-cyan-600 to-indigo-600 rounded-full flex items-center justify-center text-white text-2xl font-black mx-auto shadow-xl group-hover:scale-110 transition-transform duration-300">
                    4
                  </div>
                </div>

                {/* Title */}
                <h4 className="text-xl font-bold text-gray-900 mb-2 text-center" style={{ fontFamily: 'Sora, sans-serif' }}>
                  Handoff & Support
                </h4>

                {/* Subtitle */}
                <p className="text-xs text-cyan-600 font-semibold mb-4 md:mb-5 text-center">Included in every project</p>

                {/* Divider */}
                <div className="h-px mb-4 md:mb-5 bg-gradient-to-r from-transparent via-cyan-300 to-transparent"></div>

                {/* Features */}
                <div className="space-y-3 flex-grow">
                  <div className="flex items-start gap-2">
                    <svg className="w-5 h-5 md:w-4 md:h-4 text-cyan-600 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    <span className="text-sm text-gray-700">Complete documentation</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <svg className="w-5 h-5 md:w-4 md:h-4 text-cyan-600 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    <span className="text-sm text-gray-700">Team training sessions</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <svg className="w-5 h-5 md:w-4 md:h-4 text-cyan-600 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    <span className="text-sm text-gray-700">30-day support included</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <svg className="w-5 h-5 md:w-4 md:h-4 text-cyan-600 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    <span className="text-sm text-gray-700">Optional retainer packages</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Zykain Section */}
      <section
        className="pt-12 pb-24 lg:pt-20 lg:pb-40 relative overflow-hidden"
        style={{
          background: "linear-gradient(135deg, #16163F 0%, #1e1e4f 50%, #16163F 100%)"
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center mb-10">
            <div className="inline-flex items-center gap-2 px-6 py-3 bg-white/10 backdrop-blur-md rounded-full border border-white/20 shadow-xl mb-8">
              <svg className="w-4 h-4 text-cyan-300" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              <span className="font-bold tracking-wide text-cyan-300" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>WHY CHOOSE ZYKAIN?</span>
            </div>
            <h2 className="text-5xl lg:text-7xl font-black text-white mb-6 leading-tight" style={{ fontFamily: 'Sora, sans-serif' }}>
              Proven <span className="bg-gradient-to-r from-cyan-300 via-blue-200 to-cyan-300 bg-clip-text text-transparent">Expertise</span> &<br/>
              <span className="bg-gradient-to-r from-fuchsia-300 via-pink-200 to-fuchsia-300 bg-clip-text text-transparent">Real Results</span>
            </h2>
            <p className="text-2xl text-indigo-200 max-w-3xl mx-auto leading-relaxed" style={{ fontFamily: 'Space Grotesk, sans-serif', fontWeight: 400 }}>
              Track record that speaks for itself
            </p>
          </div>

          {/* Slider */}
          <div className="relative max-w-4xl mx-auto">
            <WhyChooseSlider />
          </div>
        </div>
      </section>

      {/* About Section */}
      {/* <section id="about" className="py-20 lg:py-32 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h2 className="text-sm font-semibold text-violet-600 tracking-wide uppercase mb-4" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              ABOUT US
            </h2>
            <h3 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-8" style={{ fontFamily: 'Sora, sans-serif' }}>
              Innovative Solutions, Seamless Experiences
            </h3>
            <p
              className="text-gray-600 mb-16 max-w-3xl mx-auto"
              style={{
                fontFamily: "Avenir Light, Avenir, sans-serif",
                fontSize: "18px",
                fontWeight: "300",
                lineHeight: "1.4",
              }}
            >
              At Zykain, we are dedicated to pushing the boundaries of app
              innovation. Our passion for creating exceptional digital
              experiences drives us to deliver cutting-edge solutions that
              resonate with users and transform businesses.
            </p>

            {/* Meet the Founders Section */}
            {/* <h4 className="text-sm font-semibold text-violet-600 tracking-wide uppercase mb-12" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              MEET THE FOUNDERS
            </h4>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-16 lg:gap-20 max-w-5xl mx-auto"> */}
              {/* Sheetal Kain */}
              {/* <div className="text-center">
                <div className="w-32 h-32 rounded-full mx-auto mb-6 overflow-hidden">
                  <img
                    src="/images/sheetal-kain.png"
                    alt="Sheetal Kain"
                    className="w-full h-full object-cover"
                    style={{
                      filter: "grayscale(100%)",
                      transform: "rotate(-3deg)",
                    }}
                  />
                </div>
                <h5 className="text-xl font-semibold text-gray-900 mb-2" style={{ fontFamily: 'Sora, sans-serif' }}>
                  Sheetal Kain
                </h5>
                <p className="text-violet-600 font-medium mb-3" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                  Co-Founder & CEO
                </p>
                <p className="text-gray-600 text-sm leading-relaxed">
                  Operations and business leader with 10+ years building efficient
                  systems and processes. Now leading ZYKAIN's mission to make
                  enterprise project management tools accessible and effective for
                  growing teams—starting with Jira optimization.
                  <br/><br/>
                  Sheetal manages client relationships, project delivery, and business
                  operations, ensuring every engagement delivers measurable results
                  for our clients.
                </p>
              </div> */}

              {/* M Anand */}
              {/* <div className="text-center">
                <div className="w-32 h-32 rounded-full mx-auto mb-6 overflow-hidden">
                  <img
                    src="/images/m-anand.png"
                    alt="M Anand"
                    className="w-full h-full object-cover"
                    style={{
                      filter: "grayscale(100%)",
                      transform: "rotate(3deg)",
                      objectPosition: "center 20%",
                      scale:"1"
                    }}
                  />
                </div>
                <h5 className="text-xl font-semibold text-gray-900 mb-2" style={{ fontFamily: 'Sora, sans-serif' }}>
                  M Anand
                </h5>
                <p className="text-violet-600 font-medium mb-3" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                  Co-Founder & CTO
                </p>
                <p className="text-gray-600 text-sm leading-relaxed">
                  Former Senior Technical Program Manager at Amazon with 14+ years
                  scaling products from zero to millions of users. MBA in Marketing
                  and B.Tech expertise driving ZYKAIN's technical innovation and
                  strategic product development in the competitive app marketplace.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section> */}

      {/* Portfolio Section */}
      {/* <section id="portfolio" className="py-12 lg:py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-sm font-semibold text-violet-600 tracking-wide uppercase mb-4" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
            OUR PORTFOLIO
          </h2>
          <h3 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-8" style={{ fontFamily: 'Sora, sans-serif' }}>
            Featured Projects
          </h3>
          <p className="text-gray-600 max-w-2xl mx-auto">
            Explore our collection of successful app developments and see how
            we've helped businesses achieve their digital transformation goals.
          </p>
        </div>
      </section> */}

      {/* FAQ Section */}
      <section id="faq" className="pt-12 pb-24 lg:pt-20 lg:pb-40 bg-gray-50 scroll-mt-[20px]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <div className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-indigo-100/80 via-violet-100/80 to-fuchsia-100/80 backdrop-blur-md rounded-full border border-indigo-200/50 shadow-xl mb-8">
              <svg className="w-4 h-4 text-indigo-600" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-8-3a1 1 0 00-.867.5 1 1 0 11-1.731-1A3 3 0 0113 8a3.001 3.001 0 01-2 2.83V11a1 1 0 11-2 0v-1a1 1 0 011-1 1 1 0 100-2zm0 8a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
              </svg>
              <span className="font-bold tracking-wide text-indigo-700" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>FREQUENTLY ASKED QUESTIONS</span>
            </div>
            <h2 className="text-5xl lg:text-7xl font-black text-gray-900 mb-6 leading-tight" style={{ fontFamily: 'Sora, sans-serif' }}>
              Got <span className="bg-gradient-to-r from-indigo-600 via-violet-600 to-fuchsia-600 bg-clip-text text-transparent">Questions?</span><br/>
              We've Got <span className="bg-gradient-to-r from-cyan-600 via-blue-600 to-indigo-600 bg-clip-text text-transparent">Answers</span>
            </h2>
            <p className="text-2xl text-gray-600 max-w-3xl mx-auto leading-relaxed" style={{ fontFamily: 'Space Grotesk, sans-serif', fontWeight: 400 }}>
              Everything you need to know about our services
            </p>
          </div>

          <div className="space-y-6 mt-16">
            {/* Q1 */}
            <div className="group relative rounded-3xl transition-all duration-300 hover:shadow-xl overflow-hidden bg-white border border-gray-200">
              <div className="p-8">
                <h4 className="text-xl font-bold text-gray-900 mb-4 flex items-start gap-3" style={{ fontFamily: 'Sora, sans-serif' }}>
                  <svg className="w-6 h-6 text-indigo-600 mt-1 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-8-3a1 1 0 00-.867.5 1 1 0 11-1.731-1A3 3 0 0113 8a3.001 3.001 0 01-2 2.83V11a1 1 0 11-2 0v-1a1 1 0 011-1 1 1 0 100-2zm0 8a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
                  </svg>
                  Do you only work with Jira Cloud or also Jira Server/Data Center?
                </h4>
                <p className="text-gray-700 leading-relaxed pl-9" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                  We work with all Jira deployment types—Cloud, Server, and Data Center. Our expertise spans all versions.
                </p>
              </div>
            </div>

            {/* Q2 */}
            <div className="group relative rounded-3xl transition-all duration-300 hover:shadow-xl overflow-hidden bg-white border border-gray-200">
              <div className="p-8">
                <h4 className="text-xl font-bold text-gray-900 mb-4 flex items-start gap-3" style={{ fontFamily: 'Sora, sans-serif' }}>
                  <svg className="w-6 h-6 text-indigo-600 mt-1 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-8-3a1 1 0 00-.867.5 1 1 0 11-1.731-1A3 3 0 0113 8a3.001 3.001 0 01-2 2.83V11a1 1 0 11-2 0v-1a1 1 0 011-1 1 1 0 100-2zm0 8a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
                  </svg>
                  How long does a typical project take?
                </h4>
                <p className="text-gray-700 leading-relaxed pl-9" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                  Most projects range from 1-3 weeks depending on complexity. A simple dashboard setup might take 1 week,
                  while a complete workflow redesign with integrations could take 3-4 weeks.
                </p>
              </div>
            </div>

            {/* Q3 */}
            <div className="group relative rounded-3xl transition-all duration-300 hover:shadow-xl overflow-hidden bg-white border border-gray-200">
              <div className="p-8">
                <h4 className="text-xl font-bold text-gray-900 mb-4 flex items-start gap-3" style={{ fontFamily: 'Sora, sans-serif' }}>
                  <svg className="w-6 h-6 text-indigo-600 mt-1 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-8-3a1 1 0 00-.867.5 1 1 0 11-1.731-1A3 3 0 0113 8a3.001 3.001 0 01-2 2.83V11a1 1 0 11-2 0v-1a1 1 0 011-1 1 1 0 100-2zm0 8a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
                  </svg>
                  Do you provide ongoing support after project completion?
                </h4>
                <p className="text-gray-700 leading-relaxed pl-9" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                  Yes! Every project includes 30 days of post-implementation support. We also offer monthly retainer
                  packages for ongoing optimization and support.
                </p>
              </div>
            </div>

            {/* Q4 */}
            <div className="group relative rounded-3xl transition-all duration-300 hover:shadow-xl overflow-hidden bg-white border border-gray-200">
              <div className="p-8">
                <h4 className="text-xl font-bold text-gray-900 mb-4 flex items-start gap-3" style={{ fontFamily: 'Sora, sans-serif' }}>
                  <svg className="w-6 h-6 text-indigo-600 mt-1 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-8-3a1 1 0 00-.867.5 1 1 0 11-1.731-1A3 3 0 0113 8a3.001 3.001 0 01-2 2.83V11a1 1 0 11-2 0v-1a1 1 0 011-1 1 1 0 100-2zm0 8a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
                  </svg>
                  Can you help us migrate from other tools (Linear, Asana, Trello) to Jira?
                </h4>
                <p className="text-gray-700 leading-relaxed pl-9" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                  Absolutely. We specialize in migrations and can help you move from other project management tools to
                  Jira while preserving your data and setting you up for success.
                </p>
              </div>
            </div>

            {/* Q5 */}
            <div className="group relative rounded-3xl transition-all duration-300 hover:shadow-xl overflow-hidden bg-white border border-gray-200">
              <div className="p-8">
                <h4 className="text-xl font-bold text-gray-900 mb-4 flex items-start gap-3" style={{ fontFamily: 'Sora, sans-serif' }}>
                  <svg className="w-6 h-6 text-indigo-600 mt-1 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-8-3a1 1 0 00-.867.5 1 1 0 11-1.731-1A3 3 0 0113 8a3.001 3.001 0 01-2 2.83V11a1 1 0 11-2 0v-1a1 1 0 011-1 1 1 0 100-2zm0 8a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
                  </svg>
                  Do you work with teams outside of India?
                </h4>
                <p className="text-gray-700 leading-relaxed pl-9" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                  Yes, we work with teams globally. We're experienced in collaborating across time zones and can
                  accommodate your schedule.
                </p>
              </div>
            </div>

            {/* Q6 */}
            <div className="group relative rounded-3xl transition-all duration-300 hover:shadow-xl overflow-hidden bg-white border border-gray-200">
              <div className="p-8">
                <h4 className="text-xl font-bold text-gray-900 mb-4 flex items-start gap-3" style={{ fontFamily: 'Sora, sans-serif' }}>
                  <svg className="w-6 h-6 text-indigo-600 mt-1 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-8-3a1 1 0 00-.867.5 1 1 0 11-1.731-1A3 3 0 0113 8a3.001 3.001 0 01-2 2.83V11a1 1 0 11-2 0v-1a1 1 0 011-1 1 1 0 100-2zm0 8a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
                  </svg>
                  What's included in the free consultation?
                </h4>
                <p className="text-gray-700 leading-relaxed pl-9" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                  A 30-minute video call where we discuss your Jira challenges, assess your current setup, and recommend
                  potential solutions. No obligation to proceed.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Form Section */}
      <section
        id="contact"
        className="pt-12 pb-24 lg:pt-20 lg:pb-40 relative overflow-hidden scroll-mt-[20px]"
        style={{ backgroundColor: "#faf9fc" }}
      >
        {/* Grid Pattern with top fade */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            backgroundImage: `
              linear-gradient(rgba(0, 0, 0, 0.03) 1px, transparent 1px),
              linear-gradient(90deg, rgba(0, 0, 0, 0.03) 1px, transparent 1px)
            `,
            backgroundSize: "50px 50px",
            mask: "linear-gradient(to bottom, transparent 0%, rgba(0,0,0,0.2) 5%, rgba(0,0,0,0.8) 10%, rgba(0,0,0,1) 15%, rgba(0,0,0,1) 100%)",
            WebkitMask:
              "linear-gradient(to bottom, transparent 0%, rgba(0,0,0,0.2) 5%, rgba(0,0,0,0.8) 10%, rgba(0,0,0,1) 15%, rgba(0,0,0,1) 100%)",
          }}
        ></div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-start">
            {/* Contact Information */}
            <div className="text-center lg:text-left">
              {/* Section Header */}
              <div className="mb-10">
                <div className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-indigo-100/80 via-violet-100/80 to-fuchsia-100/80 backdrop-blur-md rounded-full border border-indigo-200/50 shadow-xl mb-8">
                  <svg className="w-4 h-4 text-indigo-600" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
                    <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
                  </svg>
                  <span className="font-bold tracking-wide text-indigo-700" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>GET IN TOUCH</span>
                </div>
                <h2 className="text-4xl lg:text-6xl font-black text-gray-900 mb-6 leading-tight" style={{ fontFamily: 'Sora, sans-serif' }}>
                  Ready to <span className="bg-gradient-to-r from-indigo-600 via-violet-600 to-fuchsia-600 bg-clip-text text-transparent">Optimize</span><br/>
                  Your <span className="bg-gradient-to-r from-cyan-600 via-blue-600 to-indigo-600 bg-clip-text text-transparent">Jira?</span>
                </h2>
                <p className="text-xl text-gray-600 leading-relaxed mb-12" style={{ fontFamily: 'Space Grotesk, sans-serif', fontWeight: 400 }}>
                  Book a free 30-minute consultation. No pressure, just honest advice.
                </p>
              </div>

              <div className="space-y-6 mb-12">
                <div>
                  <p className="text-gray-600 text-lg content-text">
                    Remote-first company
                    <br />
                    Based out of Delhi-NCR, India
                  </p>
                </div>
                <div>
                  <p className="text-gray-600 text-lg content-text">
                    hello@zykain.com
                  </p>
                </div>
              </div>

              {/* Social Media Icons */}
              <div className="flex justify-center lg:justify-start space-x-6">
                <a
                  href="https://linkedin.com/company/zykain"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-gray-600 hover:text-violet-600 transition-colors"
                  onClick={() => trackExternalLink('https://linkedin.com/company/zykain', 'LinkedIn - Contact Section')}
                >
                  <svg
                    className="w-6 h-6"
                    fill="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
                  </svg>
                </a>
                <a
                  href="https://twitter.com/zykain"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-gray-600 hover:text-violet-600 transition-colors"
                  onClick={() => trackExternalLink('https://twitter.com/zykain', 'Twitter - Contact Section')}
                >
                  <svg
                    className="w-6 h-6"
                    fill="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path d="M24 4.557c-.883.392-1.832.656-2.828.775 1.017-.609 1.798-1.574 2.165-2.724-.951.564-2.005.974-3.127 1.195-.897-.957-2.178-1.555-3.594-1.555-3.179 0-5.515 2.966-4.797 6.045-4.091-.205-7.719-2.165-10.148-5.144-1.29 2.213-.669 5.108 1.523 6.574-.806-.026-1.566-.247-2.229-.616-.054 2.281 1.581 4.415 3.949 4.89-.693.188-1.452.232-2.224.084.626 1.956 2.444 3.379 4.6 3.419-2.07 1.623-4.678 2.348-7.29 2.04 2.179 1.397 4.768 2.212 7.548 2.212 9.142 0 14.307-7.721 13.995-14.646.962-.695 1.797-1.562 2.457-2.549z" />
                  </svg>
                </a>
                <a
                  href="https://instagram.com/zykain"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-gray-600 hover:text-violet-600 transition-colors"
                  onClick={() => trackExternalLink('https://instagram.com/zykain', 'Instagram - Contact Section')}
                >
                  <svg
                    className="w-6 h-6"
                    fill="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
                  </svg>
                </a>
              </div>
            </div>

            {/* Contact Form */}
            <div className="w-full max-w-lg mx-auto lg:max-w-none lg:mx-0 mt-16 lg:mt-20">
              {submitStatus === "error" && (
                <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-red-800 text-sm">
                    ❌ Sorry, there was an error sending your message. Please
                    try again or contact us directly at hello@zykain.com.
                  </p>
                </div>
              )}

              <form onSubmit={handleFormSubmit}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                  <div>
                    <label
                      htmlFor="firstName"
                      className="block text-sm font-medium text-gray-700 mb-2 content-text"
                    >
                      First Name *
                    </label>
                    <input
                      type="text"
                      id="firstName"
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleInputChange}
                      required
                      disabled={isSubmitting}
                      className="w-full px-0 py-3 border-0 border-b border-gray-400 focus:border-violet-600 focus:outline-none bg-transparent text-gray-900 placeholder-gray-500 content-text disabled:opacity-50"
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="lastName"
                      className="block text-sm font-medium text-gray-700 mb-2 content-text"
                    >
                      Last Name *
                    </label>
                    <input
                      type="text"
                      id="lastName"
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleInputChange}
                      required
                      disabled={isSubmitting}
                      className="w-full px-0 py-3 border-0 border-b border-gray-400 focus:border-violet-600 focus:outline-none bg-transparent text-gray-900 placeholder-gray-500 content-text disabled:opacity-50"
                    />
                  </div>
                </div>

                <div className="mb-6">
                  <label
                    htmlFor="email"
                    className="block text-sm font-medium text-gray-700 mb-2 content-text"
                  >
                    Email *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    disabled={isSubmitting}
                    className="w-full px-0 py-3 border-0 border-b border-gray-400 focus:border-violet-600 focus:outline-none bg-transparent text-gray-900 placeholder-gray-500 content-text disabled:opacity-50"
                  />
                </div>

                <div className="mb-8">
                  <label
                    htmlFor="message"
                    className="block text-sm font-medium text-gray-700 mb-2 content-text"
                  >
                    Leave us a message...
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    rows={6}
                    disabled={isSubmitting}
                    className="w-full px-0 py-3 border-0 border-b border-gray-400 focus:border-violet-600 focus:outline-none bg-transparent text-gray-900 placeholder-gray-500 resize-none content-text disabled:opacity-50"
                  ></textarea>
                </div>

                <div className="text-center lg:text-left">
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="bg-gradient-to-r from-indigo-600 via-violet-600 to-fuchsia-600 text-white px-8 py-3 rounded-full hover:from-indigo-700 hover:via-violet-700 hover:to-fuchsia-700 transition-all duration-300 font-semibold text-lg shadow-lg hover:shadow-xl transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                  >
                    {isSubmitting ? "Sending..." : "Submit"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer
        className="text-white relative overflow-hidden pb-8 pt-14 md:pb-10 md:pt-14"
        style={{
          background: "linear-gradient(to bottom, #0f0f23 0%, #1a1a3e 50%, #0f0f23 100%)"
        }}
      >
        {/* Subtle Pattern Overlay */}
        <div
          className="absolute inset-0 opacity-5"
          style={{
            backgroundImage: `radial-gradient(circle at 2px 2px, rgba(255, 255, 255, 0.15) 1px, transparent 0)`,
            backgroundSize: '32px 32px'
          }}
        ></div>
        <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 items-start">
            {/* Left Column - Company Info */}
            <div className="text-left md:text-left text-center self-center">
              <p className="text-white/70 mb-0" style={{ fontFamily: 'Space Grotesk, sans-serif', fontSize: '0.875rem', fontWeight: 500, lineHeight: 1.4 }}>
                Zykain Innovation Technologies Private Limited
              </p>
              <p className="text-white/50 mb-0" style={{ fontFamily: 'Space Grotesk, sans-serif', fontSize: '0.75rem', lineHeight: 1.4 }}>
                CIN - U62099UP2025PTC230031
              </p>
            </div>

            {/* Center Column - Logo and Copyright */}
            <div className="flex flex-col items-center gap-2 text-center">
              <a href="/" className="flex items-center gap-3 mb-0 no-underline cursor-pointer transition-transform duration-300 hover:scale-105">
                <img src="/zykain_logo_dark.png" alt="Zykain Logo" style={{ height: '28px', width: 'auto' }} />
                <span className="text-2xl font-black bg-gradient-to-r from-cyan-300 via-blue-200 to-cyan-300 bg-clip-text text-transparent" style={{ fontFamily: 'Sora, sans-serif' }}>
                  Zykain
                </span>
              </a>
              <p className="text-white/40 mb-0" style={{ fontFamily: 'Space Grotesk, sans-serif', fontSize: '0.75rem' }}>
                © 2025 Zykain. All rights reserved.
              </p>
            </div>

            {/* Right Column - Social Media and Links */}
            <div className="flex flex-col items-end md:items-end items-center gap-2 text-right md:text-right text-center">
              {/* Social Media Icons */}
              <div className="flex justify-end gap-4">
                <a
                  href="https://linkedin.com/company/zykain"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-white/60 hover:text-cyan-300 transition-all duration-300 hover:scale-110"
                  aria-label="LinkedIn"
                  onClick={() => trackExternalLink('https://linkedin.com/company/zykain', 'LinkedIn - Footer')}
                >
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
                  </svg>
                </a>
                <a
                  href="https://twitter.com/zykain"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-white/60 hover:text-cyan-300 transition-all duration-300 hover:scale-110"
                  aria-label="X (Twitter)"
                  onClick={() => trackExternalLink('https://twitter.com/zykain', 'Twitter - Footer')}
                >
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                  </svg>
                </a>
                <a
                  href="https://instagram.com/zykain"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-white/60 hover:text-cyan-300 transition-all duration-300 hover:scale-110"
                  aria-label="Instagram"
                  onClick={() => trackExternalLink('https://instagram.com/zykain', 'Instagram - Footer')}
                >
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
                  </svg>
                </a>
              </div>

              {/* Quick Links */}
              <div className="flex justify-end items-center gap-4 flex-wrap">
                <a
                  href="https://blog.zykain.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-white/60 hover:text-cyan-300 transition-colors duration-300"
                  style={{ fontFamily: 'Space Grotesk, sans-serif', fontSize: '0.8125rem' }}
                  onClick={() => trackExternalLink('https://blog.zykain.com', 'Blog - Footer')}
                >
                  Blog
                </a>
                <span className="text-white/30">•</span>
                <a
                  href="/privacy-policy.html"
                  className="text-white/60 hover:text-cyan-300 transition-colors duration-300"
                  style={{ fontFamily: 'Space Grotesk, sans-serif', fontSize: '0.8125rem' }}
                >
                  Privacy Policy
                </a>
                <span className="text-white/30">•</span>
                <a
                  href="/security-policy.html"
                  className="text-white/60 hover:text-cyan-300 transition-colors duration-300"
                  style={{ fontFamily: 'Space Grotesk, sans-serif', fontSize: '0.8125rem' }}
                >
                  Security Policy
                </a>
              </div>
            </div>
          </div>
        </div>
      </footer>

      {/* Tawk.to Live Chat Widget */}
      <TawkTo />
    </div>
  );
};

export default ZykainWebsite;